import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FAIcon } from "../../components/FAIcon";
import * as React from "react";
import { Button, CodeEditor, FormField, IconButton, Input, Label, MarkdownPreview, SearchBox, Select, SkeletonLoader, SlideInPanel, TextArea, useAlert, useDebounce, useToast } from "uxp/components";
import { hasValue, tryParseJSON } from "../../../../util";
import { generateSchema } from "../../json-schema-tools";
import { WidgetDesignerPillInput } from "../../pill-input/PillInput";
import { createDataSourceFromAction, DEFAULT_ICON, executeAndBind, getSchemaFromFields, ICollection, ICustomJsonDataSource, IDataSource, IModelActionDataSource, IQueryDataSource, ModelDefinition } from "../datasource";
import { LucyQueryBuilderDataSourcePicker } from "../lucy-query-builder/LucyQueryBuilderSourceEditor";
import { useQEServices } from "../lucy-query-builder/useQEServices";
import { getQueryPreviewComponent } from "../source-picker-helper";
import { buildFieldsDoc, newJsonSource, normalizeRows, previewName, stringifyCell, synthPlaceholderRows } from "./model-action-browser-helpers";
import { Card } from "./model-action-browser-card";
import { Category, IModelAction, IModelActionBrowserProps, ISelected, Step } from "./model-action-browser-types";
import '../DataSourcePicker.scss';

// Category / Step / ISelected / IModelActionBrowserProps / IModelAction moved to
// ./model-action-browser-types; the pure preview/JSON helpers moved to
// ./model-action-browser-helpers; the Card component moved to
// ./model-action-browser-card (all imported above).

export const DataSourceBrowser: React.FunctionComponent<IModelActionBrowserProps> = (props) => {

    const alerts = useAlert()
    const toast = useToast()
    const qeServices = useQEServices(props.context)

    // The picker opens on the Lucy Actions category by default — its action list
    // is visible (and SearchModels fires) the moment the panel mounts.
    const [step, setStep] = React.useState<Step>('lucy-action');
    // Which sidebar category is highlighted. The selected card's detail lives
    // inside the selected category's pane (master-detail split).
    const [category, setCategory] = React.useState<Category>('lucy-action');
    // The last NON-engine category selected — where the engine (New Query) close
    // button returns to. Defaults to Lucy Actions.
    const [lastCategory, setLastCategory] = React.useState<Category>('lucy-action');
    const [modelSearchText, setModelSearchText] = React.useState('');
    const [recommended, setRecommended] = React.useState(true);
    const [searchResults, setSearchResults] = React.useState<IDataSource[]>([]);
    const [paramSource, setParamSource] = React.useState<IModelActionDataSource | null>(null);
    const [jsonSource, setJsonSource] = React.useState<ICustomJsonDataSource | null>(null);
    const [jsonText, setJsonText] = React.useState('{}');

    // ---- database step state ----
    const [dbProviders, setDbProviders] = React.useState<any[]>([]);
    const [dbSources, setDbSources] = React.useState<ICollection[]>([]);
    const [dbSearch, setDbSearch] = React.useState('');
    const [dbLoading, setDbLoading] = React.useState(false);

    // ---- saved-query step state ----
    const [savedQueries, setSavedQueries] = React.useState<any[]>([]);
    const [savedSearch, setSavedSearch] = React.useState('');
    const [savedLoading, setSavedLoading] = React.useState(false);
    // ID of the currently-selected saved query record (rail highlight).
    const [selectedQueryId, setSelectedQueryId] = React.useState<string | null>(null);

    // ---- master-detail selection + preview state ----
    // `selected` marks the active category's picked card (drives the split layout
    // + the detail-panel preview). `previewNonce` is bumped to (re-)run the
    // preview: on card pick, edit-entry, rail-card switch and manual refresh —
    // NOT on param edits (those wait for the explicit Refresh preview button).
    const [selected, setSelected] = React.useState<ISelected | null>(null);
    const [previewNonce, setPreviewNonce] = React.useState(0);
    const [previewRows, setPreviewRows] = React.useState<any[]>([]);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [previewError, setPreviewError] = React.useState(false);

    // ---- saved-query preview / edit (engine QueryPreview) state ----
    // The saved query record (Key/ID/Name/Description/Configuration) under preview.
    const [editingRecord, setEditingRecord] = React.useState<any>(null);
    // The recipe initSource emitted (byte-identical) when "Use this query" confirms.
    const [savedInitSource, setSavedInitSource] = React.useState<any>(null);
    // Full pipeline seeded into the update-mode editor (query-edit step).
    const [editSource, setEditSource] = React.useState<IQueryDataSource | null>(null);
    // Bumped after a successful update to remount QueryPreview with fresh data.
    const [previewRefreshKey, setPreviewRefreshKey] = React.useState(0);

    const showQuery = !!props.enableQuery && !!props.onSelectQuery;
    // Database is not gated on the query builder (hasQB); it needs only the
    // processSource funnel to emit the canvas-shaped collection/external initSource.
    const showDatabase = !!props.onSelectQuery;

    const debouncedSearchText = useDebounce(modelSearchText, 500);
    const debouncedDbSearch = useDebounce(dbSearch, 500);

    React.useEffect(() => {
        if (step !== 'lucy-action') return;
        props.context.executeService('UXP', 'WidgetTemplate:SearchModels', { q: debouncedSearchText, max: 20, recommended: recommended ? '1' : '0' }, { json: true })
            .then((data: IModelAction[]) => {
                setSearchResults((data || []).map((x: any) => createDataSourceFromAction(x as ModelDefinition)).filter(Boolean));
            });
    }, [debouncedSearchText, recommended, step]);

    // Load providers when entering the database step (canvas parity).
    React.useEffect(() => {
        if (step !== 'database') return;
        props.context.executeService('UXP', 'WidgetTemplate:GetDataSourceProvidersForCanvas', {}, { json: true })
            .then((data: any[]) => setDbProviders(data || []))
            .catch(() => setDbProviders([]));
    }, [step]);

    // Load collections + external databases (server-filtered collections; external
    // DBs are matched against providers then client-filtered by the search text).
    React.useEffect(() => {
        if (step !== 'database') return;
        let cancelled = false;
        (async () => {
            setDbLoading(true);
            try {
                let dbs: ICollection[] = [];
                dbs = await loadCollections(dbs);
                dbs = await loadExternalDatabases(dbs);
                if (!cancelled) setDbSources(dbs);
            } finally {
                if (!cancelled) setDbLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [step, debouncedDbSearch, dbProviders]);

    // Load the full saved-query list when entering the saved-query step.
    React.useEffect(() => {
        if (step !== 'query-saved') return;
        setSavedLoading(true);
        props.context.executeService('Lucy', 'Query:All', { max: 100 }, { json: true })
            .then((res: any[]) => setSavedQueries(res || []))
            .catch(() => setSavedQueries([]))
            .finally(() => setSavedLoading(false));
    }, [step]);

    // Edit entry: when opened on a bound source, jump straight to the right
    // category IN master-detail mode with that source selected (detail + preview
    // visible). Back navigation from the detail returns to the category grid.
    React.useEffect(() => {
        const sel = props.selectedSource as IDataSource | null;
        if (!sel) return;

        if (sel.type === 'model-action') {
            setCategory('lucy-action');
            setLastCategory('lucy-action');
            setParamSource(sel as IModelActionDataSource);
            openSelection({ source: sel, confirm: 'selected' });
            setStep('lucy-action');
        }
        else if (sel.type === 'model-collection' || sel.type === 'external-database') {
            // Confirm re-runs the funnel with the original canvas-shaped initSource
            // (carried on __source__); existingDSId is selectedSource.id → replace.
            setCategory('database');
            setLastCategory('database');
            openSelection({ source: sel, confirm: 'query', initSource: (sel as any).__source__ });
            setStep('database');
        }
        else if (sel.type === 'custom-json') {
            // Prefill from the bound source — do NOT reset jsonSource here (that is
            // the fresh-source behaviour reserved for a sidebar Custom JSON click).
            setCategory('custom-json');
            setLastCategory('custom-json');
            const cj = sel as ICustomJsonDataSource;
            setJsonSource(cj);
            setJsonText(JSON.stringify(cj.json || {}));
            setStep('custom-json');
        }
        else if (sel.type === 'query') {
            const q = (sel as IQueryDataSource).query;
            const isSavedRef = !!q?.sourceOnly && typeof q?.sources?.[0]?.id === 'string' && q.sources[0].id.startsWith('query');
            if (isSavedRef) {
                setCategory('query-saved');
                setLastCategory('query-saved');
                // When the engine QueryPreview is available, resolve the saved query
                // record and open the rich preview (with Edit → Query:Update).
                // Otherwise keep the generic fallback preview.
                if (getQueryPreviewComponent()) {
                    openBoundSavedQueryPreview(sel as IQueryDataSource, q);
                }
                else {
                    const initSource = {
                        type: 'query',
                        name: (sel as IQueryDataSource).name,
                        schema: sel.schema,
                        query: q,
                        description: sel.description || ''
                    };
                    openSelection({ source: sel, confirm: 'query', initSource });
                }
                setStep('query-saved');
            }
            else {
                // inline pipeline → open the Data Explorer editor directly
                setCategory('query-new');
                setStep('query-new');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedSource]);

    // Detail-panel preview: run the (temp) selected source through executeAndBind
    // and render the resulting rows. On empty/error, synthesize placeholder rows
    // from schema. Re-runs only when previewNonce changes (auto-run on selection,
    // manual Refresh) — never on live param edits.
    React.useEffect(() => {
        const src = selected?.source;
        if (!src) { setPreviewRows([]); setPreviewLoading(false); setPreviewError(false); return; }

        let cancelled = false;
        (async () => {
            setPreviewLoading(true);
            setPreviewError(false);
            try {
                const tempId = src.id || 'preview';
                const tempSource = { ...src, id: tempId } as IDataSource;
                const result = await executeAndBind(props.context, { source: tempId, path: '$' }, [tempSource], {}, props.contextData);
                let rows = normalizeRows(result);
                if (!rows.length) rows = synthPlaceholderRows(src.schema);
                if (!cancelled) setPreviewRows(rows);
            }
            catch (e) {
                if (!cancelled) { setPreviewError(true); setPreviewRows(synthPlaceholderRows(src.schema)); }
            }
            finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previewNonce]);

    async function loadCollections(databases: ICollection[]): Promise<ICollection[]> {
        try {
            const collections = await props.context.executeService('UXP', 'WidgetTemplate:GetDataSourceProviderDatabasesForCanvas',
                { query: debouncedDbSearch, max: 20, modelKey: '' }, { json: true });
            (collections || []).forEach((x: any) => {
                const attributes = tryParseJSON(x.Attributes);
                databases.push({
                    Name: x.Name,
                    Action: x.Action,
                    Type: 'collection',
                    MapKey: x.MapKey,
                    Description: 'A collection of documents',
                    Attributes: attributes,
                    Docs: buildFieldsDoc(attributes),
                } as ICollection);
            });
        } catch (e) { /* ignore — collections optional */ }
        return databases;
    }

    async function loadExternalDatabases(databases: ICollection[]): Promise<ICollection[]> {
        try {
            const externalDBRes = await props.context.executeComponent('ExternalDatabase', '/v1/databases', 'get', {});
            const q = (debouncedDbSearch || '').toLowerCase();
            (externalDBRes || []).forEach((c: any) => {
                const model = dbProviders.find(m => m.MapKey == c.model);
                if (!model) return;
                if (q && !`${model.Name} ${c.name}`.toLowerCase().includes(q)) return;
                const attributes = c.attributes;
                databases.push({
                    Name: model.Name,
                    Action: c.name,
                    MapKey: c.model,
                    Description: 'An external database',
                    Docs: buildFieldsDoc(attributes),
                    Type: 'external',
                    ID: c._id,
                    Attributes: attributes,
                } as ICollection);
            });
        } catch (e) { /* ignore — external DBs optional */ }
        return databases;
    }

    // Set the active selection and (re-)run its preview.
    function openSelection(sel: ISelected) {
        setSelected(sel);
        setPreviewNonce(n => n + 1);
    }

    // Clear the current master-detail selection → back to the category grid.
    function clearSelection() {
        setSelected(null);
        setParamSource(null);
        setEditingRecord(null);
        setSavedInitSource(null);
        setSelectedQueryId(null);
        setPreviewRows([]);
        setPreviewError(false);
        setPreviewLoading(false);
    }

    // Whether the active category is showing a detail panel (a card selected).
    function hasDetail(): boolean {
        if (step === 'lucy-action' || step === 'database') return !!selected;
        if (step === 'query-saved') return !!editingRecord || !!selected;
        return false;
    }

    // Sidebar category click → highlight it, clear any selection and reset its
    // pane to the category grid. Custom JSON resets to a fresh source each click.
    function selectCategory(cat: Category) {
        clearSelection();
        setCategory(cat);
        if (cat !== 'query-new') setLastCategory(cat);
        switch (cat) {
            case 'lucy-action': setStep('lucy-action'); break;
            case 'database': setStep('database'); break;
            case 'query-saved': setStep('query-saved'); break;
            case 'query-new': setStep('query-new'); break;
            case 'custom-json': startCustomJson(); break;
        }
    }

    // Closing the full-bleed engine (New Query) returns to the sidebar layout on
    // the previously selected non-engine category (default Lucy Actions).
    function closeEngineToSidebar() {
        clearSelection();
        setCategory(lastCategory);
        setStep(lastCategory);
    }

    // Top-bar confirm for the lucy-action / database detail. Emits through the
    // exact same path as before:
    //   lucy-action → props.onSelected(paramSource)   (carries the live param edits)
    //   database    → props.onSelectQuery(initSource) (untouched canvas-shaped source)
    function confirmSelection() {
        if (!selected) return;
        if (selected.confirm === 'selected') {
            if (paramSource) props.onSelected(paramSource);
            else if (selected.source) props.onSelected(selected.source);
        }
        else if (selected.confirm === 'query') {
            if (props.onSelectQuery) props.onSelectQuery(selected.initSource);
        }
    }

    // Pick a Lucy action card → open its detail (config + auto-run preview).
    function pickModelAction(sr: IDataSource) {
        setParamSource(sr as IModelActionDataSource);
        openSelection({ source: sr, confirm: 'selected' });
    }

    // Re-run the lucy-action preview with the CURRENT param edits (Refresh button).
    function refreshLucyPreview() {
        setSelected(prev => (prev ? { ...prev, source: paramSource } : prev));
        setPreviewNonce(n => n + 1);
    }

    function updateParam(index: number, value: string) {
        setParamSource(prev => {
            if (!prev) return prev;
            const parameters = prev.parameters.slice();
            parameters[index] = { ...parameters[index], value };
            return Object.assign({}, prev, { parameters });
        });
    }

    function startCustomJson() {
        const source = newJsonSource();
        setJsonSource(source);
        setJsonText(JSON.stringify(source.json || {}));
        setStep('custom-json');
    }

    // Header Save for the custom-json step. Emits the same object shape the legacy
    // DataSourceEditor emitted: the newJsonSource() fields + parsed json + genson schema.
    function saveCustomJson() {
        if (!jsonSource) return;
        let jObject: any;
        try {
            jObject = JSON.parse(jsonText);
        }
        catch (e) {
            toast.error(props.context.$L('widgetdesigner.messages.json-invalid'));
            return;
        }
        const schema = generateSchema(jObject);
        props.onSelected({ ...jsonSource, json: jObject, schema } as ICustomJsonDataSource);
    }

    // Database collections / external databases go through the SAME processSource
    // funnel as queries (props.onSelectQuery) as a canvas-shaped initSource.
    // processSource discriminates by Type ('collection' | 'external') and emits the
    // matching model-collection / external-database IDataSource, schema regenerated
    // from Attributes by extractSchemaFromInitialSource.
    async function selectDatabase(c: ICollection) {
        // Master-detail: process (without persisting) → detail preview table →
        // confirm re-runs the untouched funnel with the original initSource `c`.
        if (props.onProcessSource) {
            const processed = await props.onProcessSource(c);
            openSelection({ source: processed, confirm: 'query', initSource: c });
            return;
        }
        // Fallback (no preview funnel supplied): emit directly, legacy behaviour.
        if (props.onSelectQuery) props.onSelectQuery(c);
    }

    // Direct saved-query selection — byte-equivalent to the engine's saved-query
    // path: use the persisted schema/name from Configuration (do NOT re-derive),
    // resolve the source ref via getQueryDataSourceById, assemble the sourceOnly
    // query and emit through the same processSource funnel.
    async function selectSavedQuery(record: any) {
        const saved = tryParseJSON(record.Configuration);
        // Queries saved by the Data Explorer persist schema as a raw {name,type}[]
        // fields list; widget-designer's own save persists the converted JSON-schema
        // object. The engine path converts at selection time (getSchemaFromFields),
        // so do the same here for byte-equivalence.
        const schema = Array.isArray(saved?.schema) ? getSchemaFromFields(saved.schema) : saved?.schema;
        const name = saved?.name || record.Name;

        const getById = qeServices?.getQueryDataSourceById;
        if (!getById) {
            // Guard: fall back to the engine embed (should not happen once QB gating passed).
            setStep('query-new');
            return;
        }

        const ref = await getById(record.ID);
        const initSource: any = {
            type: 'query',
            name,
            schema,
            query: { sources: [ref], steps: [] as any[], sourceOnly: true },
            description: record.Description || ''
        };

        // When the engine QueryPreview is available, show the rich preview
        // (details + live table + Edit query) in the detail panel. "Use this query"
        // emits the exact same recipe initSource — byte-identical to the fallback.
        if (getQueryPreviewComponent()) {
            openSavedQueryPreview(record, initSource);
            return;
        }

        // Fallback (older engine bundle): process (without persisting) → generic
        // detail preview table → confirm re-runs the untouched funnel.
        if (props.onProcessSource) {
            const processed = await props.onProcessSource(initSource);
            setEditingRecord(null);
            setSavedInitSource(null);
            setSelectedQueryId(record.ID || null);
            openSelection({ source: processed, confirm: 'query', initSource });
            return;
        }
        // Fallback (no preview funnel supplied): emit directly, legacy behaviour.
        if (props.onSelectQuery) props.onSelectQuery(initSource);
    }

    // Open the engine QueryPreview in the saved-query detail panel. `initSource` is
    // the recipe emitted (byte-identical) when "Use this query" confirms.
    function openSavedQueryPreview(record: any, initSource: any) {
        setSelected(null);
        setEditingRecord(record);
        setSavedInitSource(initSource);
        setSelectedQueryId(record?.ID || null);
    }

    // Edit-entry for a BOUND saved-query source (white-box click): resolve the
    // record via Query:All by matching the ID parsed from the sourceOnly ref
    // ('query:<Name>/<ID>' → segment after the last '/'), then open the rich
    // preview. Falls back to the generic preview when the record can't be resolved.
    async function openBoundSavedQueryPreview(sel: IQueryDataSource, q: any) {
        const refId = String(q?.sources?.[0]?.id || '').split('/').pop();
        let record: any = null;
        try {
            const records: any[] = await props.context.executeService('Lucy', 'Query:All', { max: 100 }, { json: true });
            record = (records || []).find(r => r.ID === refId) || null;
        } catch (e) { record = null; }

        if (record) {
            const saved = tryParseJSON(record.Configuration);
            const schema = Array.isArray(saved?.schema) ? getSchemaFromFields(saved.schema) : saved?.schema;
            const name = saved?.name || record.Name;
            const initSource: any = {
                type: 'query',
                name,
                schema,
                query: q,
                description: record.Description || ''
            };
            openSavedQueryPreview(record, initSource);
            return;
        }

        // Could not resolve the record → generic preview so the user can still see
        // + re-bind the source.
        const initSource = {
            type: 'query',
            name: sel.name,
            schema: sel.schema,
            query: q,
            description: sel.description || ''
        };
        setEditingRecord(null);
        setSavedInitSource(null);
        openSelection({ source: sel, confirm: 'query', initSource });
    }

    // "Use this query" on the saved-query detail — emits the exact recipe through
    // the untouched funnel (engine path uses savedInitSource; the generic fallback
    // uses selected.initSource — both are the byte-identical recipe).
    function confirmSavedQuery() {
        const src = savedInitSource ?? selected?.initSource;
        if (src && props.onSelectQuery) props.onSelectQuery(src);
    }

    // "Edit query" on the saved-query detail → open the update-mode editor. The
    // full pipeline comes from the record's Configuration — records may store
    // `{ query, ... }` (Lucy app writer) or a full IQueryDataSource with `.query`
    // (widget-designer writer); `saved.query` covers both.
    function startEditSavedQuery() {
        if (!editingRecord) return;
        const saved = tryParseJSON(editingRecord.Configuration) || {};
        const fullQuery = saved?.query || null;
        const src: IQueryDataSource = {
            id: editingRecord.ID || '',
            type: 'query',
            // Seed from the RECORD's Name/Description — the record Name is the
            // unique key on the Query model; the Configuration's inner name can
            // differ (e.g. auto-timestamped records) and colliding with another
            // record's Name makes Query:Update fail with "Name already exists".
            name: editingRecord.Name || saved?.name || '',
            description: editingRecord.Description ?? saved?.description ?? '',
            parameters: [],
            schema: saved?.schema,
            query: fullQuery
        } as IQueryDataSource;
        setEditSource(src);
        setStep('query-edit');
    }

    // Persist the saved-query edit via the SourcePickerWrapper funnel (Query:Update).
    // On success, refresh the local record + remount the preview and return to it.
    async function handleUpdateSavedQuery(changes: { name: string, description: string, query: any, schema: any }): Promise<boolean> {
        if (!props.onUpdateQuery || !editingRecord) return false;
        const res = await props.onUpdateQuery(editingRecord, changes);
        if (res?.status === 'done') {
            if (res.record) setEditingRecord(res.record);
            setPreviewRefreshKey(k => k + 1);
            setStep('query-saved');
            return true;
        }
        return false;
    }

    const empty = (text: string) => <div className="uxp_wd-dsp__empty">{text}</div>;

    // Loading placeholder: skeleton rows sized like the list/table rows they precede.
    const loadingRows = (count: number = 5) => (
        <div className="uxp_wd-dsp__loading-rows">
            {Array(count).fill(0).map((_, i) => (
                <SkeletonLoader key={i} height="2.25rem" additionalStyle={{ marginBottom: '0.5rem' }} />
            ))}
        </div>
    );

    // ---- persistent left sidebar: flat category list (icon + label). Gating:
    // Lucy Actions + Custom JSON always; Database with the processSource funnel;
    // Saved Queries + New Query with the query builder wired.
    function renderSidebar() {
        const items: { cat: Category; icon: IconProp; label: string; show: boolean }[] = [
            { cat: 'lucy-action', icon: ['fal', 'sitemap'] as IconProp, label: props.context.$L('widgetdesigner.datasource.lucy-actions'), show: true },
            { cat: 'database', icon: ['fal', 'database'] as IconProp, label: props.context.$L('widgetdesigner.datasource.collections'), show: showDatabase },
            { cat: 'query-saved', icon: ['fal', 'folder-open'] as IconProp, label: props.context.$L('widgetdesigner.datasource.saved-queries'), show: showQuery },
            { cat: 'query-new', icon: ['fal', 'plus-circle'] as IconProp, label: props.context.$L('widgetdesigner.datasource.new-query'), show: showQuery },
            { cat: 'custom-json', icon: ['fal', 'brackets-curly'] as IconProp, label: props.context.$L('widgetdesigner.datasource.custom-json'), show: true },
        ];
        return <div className="uxp_wd-dsp__sidebar">
            <div className="uxp_wd-dsp__sidebar-label">{props.context.$L('widgetdesigner.datasource.sources')}</div>
            {items.filter(i => i.show).map(i => (
                <div
                    key={i.cat}
                    className={'uxp_wd-dsp__sidebar-item' + (category === i.cat ? ' uxp_wd-dsp__sidebar-item--selected' : '')}
                    onClick={() => selectCategory(i.cat)}
                >
                    <FAIcon className="uxp_wd-dsp__sidebar-icon" icon={i.icon} />
                    <span className="uxp_wd-dsp__sidebar-text">{i.label}</span>
                </div>
            ))}
        </div>
    }

    // Master-detail shell: a ~30% rail (search/toolbar + stacked cards) beside a
    // ~70% detail panel. Used by all three list categories once a card is picked.
    function renderSplit(railTop: React.ReactNode, railBody: React.ReactNode, detail: React.ReactNode) {
        return <div className="uxp_wd-dsp__step">
            <div className="uxp_wd-dsp__split">
                <div className="uxp_wd-dsp__rail">
                    {railTop}
                    <div className="uxp_wd-dsp__rail-list">{railBody}</div>
                </div>
                <div className="uxp_wd-dsp__detail">{detail}</div>
            </div>
        </div>;
    }

    // Shared preview block (details head + scrollable data table) rendered inside
    // a detail panel. Reuses the existing __preview-* styles.
    function renderPreviewTable(src: IDataSource | null) {
        const rows = previewRows || [];
        const columns = rows.length ? Object.keys(rows[0]).filter(k => !k.startsWith('_')) : [];
        return <div className="uxp_wd-dsp__preview">
            <div className="uxp_wd-dsp__preview-body">
                {previewLoading ? loadingRows()
                    : !src ? empty(props.context.$L('widgetdesigner.datasource.preview-unavailable'))
                        : columns.length === 0 ? empty(props.context.$L('widgetdesigner.datasource.no-preview'))
                            : <div className="uxp_wd-dsp__preview-scroll">
                                <table className="uxp_wd-dsp__preview-table">
                                    <thead>
                                        <tr>{columns.map(c => <th key={c}>{c}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {rows.slice(0, 10).map((r, ri) => (
                                            <tr key={ri}>
                                                {columns.map(c => <td key={c}>{stringifyCell(r[c])}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>}
            </div>
            {previewError && src &&
                <div className="uxp_wd-dsp__preview-note">{props.context.$L('widgetdesigner.datasource.sample-data-note')}</div>}
        </div>;
    }

    // ---- Lucy Actions ----
    function lucyToolbar() {
        return <div className="uxp_wd-dsp__toolbar">
            <div className="uxp_wd-dsp__search">
                <SearchBox value={modelSearchText} onChange={setModelSearchText} />
            </div>
            <Select
                options={[
                    { label: props.context.$L('widgetdesigner.datasource.recommended'), value: '1' },
                    { label: props.context.$L('widgetdesigner.datasource.all'), value: '0' }
                ]}
                selected={recommended ? '1' : '0'}
                onChange={(v) => setRecommended(v === '1')}
                // Minimal trigger: just the current label + a dropdown indicator.
                renderPlaceholder={{
                    renderCustomPill: () => (
                        <span className="uxp_wd-dsp__filter-pill">
                            {recommended ? props.context.$L('widgetdesigner.datasource.recommended') : props.context.$L('widgetdesigner.datasource.all')}
                            <FAIcon icon={['fal', 'chevron-down'] as IconProp} />
                        </span>
                    )
                }}
            />
        </div>;
    }

    function lucyCards() {
        return searchResults.map((sr, i) => {
            const ma = sr as IModelActionDataSource;
            return <Card
                key={i}
                icon={sr.icon || DEFAULT_ICON}
                title={ma.model}
                subtitle={hasValue(sr.description) ? sr.description : ma.action}
                onClick={() => pickModelAction(sr)}
                selected={paramSource === sr}
            />;
        });
    }

    function renderLucyDetail() {
        if (!paramSource) return null;
        return <div className="uxp_wd-dsp__detail-inner">
            <div className="uxp_wd-dsp__detail-head">
                <div className="uxp_wd-dsp__detail-titlerow">
                    <span className="uxp_wd-dsp__detail-back">
                        <IconButton icon="fal chevron-left" borderless onClick={clearSelection} />
                    </span>
                    <div className="uxp_wd-dsp__detail-title">{paramSource.model} · {paramSource.action}</div>
                </div>
                <div className="uxp_wd-dsp__docs">
                    {paramSource.description
                        ? <MarkdownPreview value={paramSource.description || ''} />
                        : <div className="uxp_wd-dsp__empty">{props.context.$L('widgetdesigner.datasource.no-description')}</div>}
                </div>
            </div>

            <div className="uxp_wd-dsp__inputs">
                {paramSource.parameters?.map((p, i) => (
                    <div className="uxp_wd-dsp__field" key={p.id || i}>
                        <label className="uxp_wd-dsp__label">{p.description}</label>
                        <WidgetDesignerPillInput
                            contextData={props.contextData}
                            value={p.value || ''}
                            onChange={(v) => updateParam(i, v)}
                            showFields
                            showThemes
                        />
                    </div>
                ))}
                {!Number(paramSource.parameters?.length) &&
                    <div className="uxp_wd-dsp__empty">{props.context.$L('widgetdesigner.datasource.no-inputs')}</div>}
            </div>

            <div className="uxp_wd-dsp__detail-actions">
                <Button title={props.context.$L('widgetdesigner.datasource.refresh-preview')} variant="secondary" size="small" onClick={refreshLucyPreview} />
            </div>

            {renderPreviewTable(paramSource)}
        </div>;
    }

    function renderLucyAction() {
        if (selected) {
            return renderSplit(
                lucyToolbar(),
                searchResults.length === 0
                    ? empty(props.context.$L('widgetdesigner.datasource.no-results'))
                    : <div className="uxp_wd-dsp__cards uxp_wd-dsp__cards--rail">{lucyCards()}</div>,
                renderLucyDetail()
            );
        }
        return <div className="uxp_wd-dsp__step">
            {lucyToolbar()}
            <div className="uxp_wd-dsp__grid">
                {searchResults.length === 0
                    ? empty(props.context.$L('widgetdesigner.datasource.no-results'))
                    : <div className="uxp_wd-dsp__cards">{lucyCards()}</div>}
            </div>
        </div>;
    }

    // ---- Database ----
    // Flat grid — no model-wise grouping. The card subtitle carries the model
    // name (the generic Description stays on the emitted initSource untouched).
    function databaseNodes(): React.ReactNode[] {
        const items = [...dbSources].sort((a, b) => (a.Name < b.Name ? -1 : (a.Name > b.Name) ? 1 : 0));
        return items.map((c, i) => (
            <Card
                key={i}
                icon="fal database"
                title={c.Action}
                subtitle={c.Name}
                onClick={() => selectDatabase(c)}
                selected={selected?.initSource === c}
            />
        ));
    }

    function renderDatabaseDetail() {
        const c = selected?.initSource;
        const src = selected?.source || null;
        return <div className="uxp_wd-dsp__detail-inner">
            <div className="uxp_wd-dsp__detail-head">
                <div className="uxp_wd-dsp__detail-titlerow">
                    <span className="uxp_wd-dsp__detail-back">
                        <IconButton icon="fal chevron-left" borderless onClick={clearSelection} />
                    </span>
                    <div className="uxp_wd-dsp__detail-title">{hasValue(c?.Action) ? c.Action : previewName(src)}</div>
                </div>
                {hasValue(c?.Name) &&
                    <div className="uxp_wd-dsp__preview-desc">{c.Name}</div>}
                {hasValue(c?.Docs) &&
                    <div className="uxp_wd-dsp__docs"><MarkdownPreview value={c.Docs || ''} /></div>}
            </div>
            {renderPreviewTable(src)}
        </div>;
    }

    function renderDatabase() {
        const search = <div className="uxp_wd-dsp__toolbar">
            <div className="uxp_wd-dsp__search"><SearchBox value={dbSearch} onChange={setDbSearch} /></div>
        </div>;
        const hasItems = dbSources.length > 0;

        if (selected) {
            return renderSplit(
                search,
                dbLoading ? loadingRows()
                    : !hasItems ? empty(props.context.$L('widgetdesigner.datasource.no-databases'))
                        : <div className="uxp_wd-dsp__cards uxp_wd-dsp__cards--rail">{databaseNodes()}</div>,
                renderDatabaseDetail()
            );
        }
        return <div className="uxp_wd-dsp__step">
            {search}
            <div className="uxp_wd-dsp__grid">
                {dbLoading ? loadingRows()
                    : !hasItems ? empty(props.context.$L('widgetdesigner.datasource.no-databases'))
                        : <div className="uxp_wd-dsp__cards">{databaseNodes()}</div>}
            </div>
        </div>;
    }

    // ---- Saved Queries ----
    function savedQueryItems() {
        const q = (savedSearch || '').toLowerCase();
        return (savedQueries || []).filter(r => !q || `${r.Name} ${r.Description || ''}`.toLowerCase().includes(q));
    }

    function savedCards(items: any[]) {
        return items.map((r, i) => (
            <Card
                key={r.ID || i}
                icon="fal folder-open"
                title={r.Name}
                subtitle={hasValue(r.Description) ? r.Description : props.context.$L('widgetdesigner.datasource.saved-query')}
                onClick={() => selectSavedQuery(r)}
                selected={selectedQueryId != null && r.ID === selectedQueryId}
            />
        ));
    }

    function renderSavedDetail() {
        const QueryPreview = getQueryPreviewComponent();
        const detailBack = (
            <span className="uxp_wd-dsp__detail-back">
                <IconButton icon="fal chevron-left" borderless onClick={clearSelection} />
            </span>
        );
        if (QueryPreview && editingRecord) {
            return <div className="uxp_wd-dsp__detail-inner uxp_wd-dsp__detail-inner--query-preview">
                <div className="uxp_wd-dsp__detail-titlerow">{detailBack}</div>
                <QueryPreview
                    key={previewRefreshKey}
                    uxpContext={props.context}
                    queryId={editingRecord.ID}
                    name={editingRecord.Name}
                    description={editingRecord.Description}
                    onEdit={startEditSavedQuery}
                />
            </div>;
        }
        // Generic fallback preview (older engine bundle).
        const src = selected?.source || null;
        const initSource = selected?.initSource;
        return <div className="uxp_wd-dsp__detail-inner">
            <div className="uxp_wd-dsp__detail-head">
                <div className="uxp_wd-dsp__detail-titlerow">
                    {detailBack}
                    <div className="uxp_wd-dsp__detail-title">{hasValue(initSource?.name) ? initSource.name : previewName(src)}</div>
                </div>
                {hasValue(initSource?.description) &&
                    <div className="uxp_wd-dsp__preview-desc">{initSource.description}</div>}
            </div>
            {renderPreviewTable(src)}
        </div>;
    }

    function renderSavedQueries() {
        const items = savedQueryItems();
        const search = <div className="uxp_wd-dsp__toolbar">
            <div className="uxp_wd-dsp__search"><SearchBox value={savedSearch} onChange={setSavedSearch} /></div>
        </div>;

        if (editingRecord || selected) {
            return renderSplit(
                search,
                savedLoading ? loadingRows()
                    : items.length === 0 ? empty(props.context.$L('widgetdesigner.datasource.no-saved-queries'))
                        : <div className="uxp_wd-dsp__cards uxp_wd-dsp__cards--rail">{savedCards(items)}</div>,
                renderSavedDetail()
            );
        }
        return <div className="uxp_wd-dsp__step">
            {search}
            <div className="uxp_wd-dsp__grid">
                {savedLoading ? loadingRows()
                    : items.length === 0 ? empty(props.context.$L('widgetdesigner.datasource.no-saved-queries'))
                        : <div className="uxp_wd-dsp__cards">{savedCards(items)}</div>}
            </div>
        </div>;
    }

    // Create-new: embed the Data Explorer INLINE (no nested Modal, no duplicate
    // close button); the picker modal goes full-screen for this step. The engine's
    // Saved Queries category stays available — picking one there seeds a NEW
    // pipeline, unlike our query-saved step which emits the sourceOnly ref directly.
    function renderQueryNew() {
        return <div className="uxp_wd-dsp__inline-qb">
            <LucyQueryBuilderDataSourcePicker
                inline
                show={true}
                selectedSource={props.selectedSource}
                onSelect={props.onSelectQuery}
                onRemove={props.onRemove}
                context={props.context}
                contextData={props.contextData}
                onClose={closeEngineToSidebar}
                onSave={props.onSaveQuery}
                canSelectSavedQuerries={true}
            />
        </div>
    }

    // Update-mode editor: edits the saved query RECORD (Query:Update) via
    // handleUpdateSavedQuery — never emits to the widget. editingQueryId lights up
    // the warning banner; saveMode='update-saved' swaps the completion path.
    // Closing returns to the saved-query detail (editingRecord is preserved).
    function renderQueryEdit() {
        return <div className="uxp_wd-dsp__inline-qb">
            <LucyQueryBuilderDataSourcePicker
                inline
                show={true}
                selectedSource={editSource as any}
                onSelect={() => { }}
                onRemove={props.onRemove}
                context={props.context}
                contextData={props.contextData}
                onClose={() => setStep('query-saved')}
                saveMode="update-saved"
                editingQueryId={editingRecord?.ID}
                onUpdateQuery={handleUpdateSavedQuery}
            />
        </div>
    }

    function renderCustomJson() {
        if (!jsonSource) return null;
        return <div className="uxp_wd-dsp__step">
            <div className="uxp_wd-dsp__json">
                <FormField>
                    <Label>{props.context.$L('widgetdesigner.common.name')}</Label>
                    <Input
                        value={jsonSource.name || ''}
                        onChange={(v) => setJsonSource(prev => (prev ? { ...prev, name: v } : prev))}
                    />
                </FormField>

                <FormField>
                    <Label>{props.context.$L('widgetdesigner.common.description')}</Label>
                    <TextArea
                        value={jsonSource.description || ''}
                        onChange={(v) => setJsonSource(prev => (prev ? { ...prev, description: v } : prev))}
                    />
                </FormField>

                <FormField className="uxp_wd-dsp__json-editor">
                    <Label>{props.context.$L('widgetdesigner.common.json')}</Label>
                    <CodeEditor
                        value={jsonText}
                        onChange={setJsonText}
                        language="json"
                    />
                </FormField>
            </div>
        </div>
    }

    function renderStep() {
        switch (step) {
            case 'lucy-action': return renderLucyAction();
            case 'database': return renderDatabase();
            case 'query-saved': return renderSavedQueries();
            case 'custom-json': return renderCustomJson();
            case 'query-new': return renderQueryNew();
            case 'query-edit': return renderQueryEdit();
            default: return renderLucyAction();
        }
    }

    // The top-bar title is STATIC — the detail panels carry their own inline
    // headings (with their own back button), so the title never changes with
    // the selection.
    function stepTitle() {
        return props.context.$L('widgetdesigner.datasource.add-title');
    }

    // v5 pattern: step action buttons live in the modal header (right-aligned,
    // before the close button) instead of a step footer. In master-detail mode the
    // confirm button only appears once a card is selected.
    function stepActions() {
        switch (step) {
            case 'lucy-action':
            case 'database':
                return selected
                    ? <Button title={props.context.$L('widgetdesigner.datasource.select-close')} variant="primary" size="small" onClick={confirmSelection} />
                    : null;
            case 'query-saved':
                return (editingRecord || selected)
                    ? <Button title={props.context.$L('widgetdesigner.datasource.select-close')} variant="primary" size="small" onClick={confirmSavedQuery} />
                    : null;
            case 'custom-json':
                return jsonSource
                    ? <Button title={props.context.$L('widgetdesigner.datasource.save-close')} variant="primary" size="small" onClick={saveCustomJson} />
                    : null;
            default: return null;
        }
    }

    // Backdrop / Escape dismiss of the unified full-screen panel. The Data Explorer
    // steps (query-new / query-edit) render full-bleed with the engine's own chrome,
    // so a dismiss there steps back within the picker (matching the engine close
    // buttons) rather than closing the whole picker; every other step closes it.
    function handlePanelDismiss() {
        if (step === 'query-new') { closeEngineToSidebar(); return; }
        if (step === 'query-edit') { setStep('query-saved'); return; }
        props.onClose();
    }

    // The Data Explorer steps host the engine full-bleed with its own header +
    // close buttons — no picker top bar / sidebar for those.
    const isExplorerStep = step === 'query-new' || step === 'query-edit';


    // ONE full-screen content-area SlideInPanel hosts everything (app header +
    // sidebar stay visible). Category steps render as [top bar] + [sidebar | pane];
    // the full-bleed engine steps (New Query / Edit Query) take over the whole
    // body with the engine's own chrome.
    return <SlideInPanel isOpen={true} onClose={handlePanelDismiss} mode="full">
        <div className="uxp_wd-dsp">
            {isExplorerStep
                ? <div className="uxp_wd-dsp__content">
                    {renderStep()}
                </div>
                : <>
                    <div className="uxp_wd-dsp__topbar">
                        <div className="uxp_wd-dsp__title">{stepTitle()}</div>
                        <div className="uxp_wd-dsp__topbar-actions">
                            {stepActions()}
                            <span className="uxp_wd-dsp__close">
                                <IconButton type="close" variant="secondary" size="small" onClick={() => props.onClose()} />
                            </span>
                        </div>
                    </div>
                    <div className="uxp_wd-dsp__layout">
                        {renderSidebar()}
                        <div className="uxp_wd-dsp__pane">
                            {renderStep()}
                        </div>
                    </div>
                </>}
        </div>
    </SlideInPanel>;
};
